import React, {
  useCallback, useEffect, useState, memo, useMemo,
} from 'react';

import { IPageRecord as IPageRecordData } from 'app/store/nodes/card/interfaces';
import { useDispatcher } from 'app/store/utils/redux/hooks';
import { useMediaResource } from 'app/lib/MediaHooks';
import { useFlipbookContext } from 'app/components/common/flipbook/lib/hooks';
import WaveSurfer from 'app/lib/WaveSurfer';

import { ReactComponent as IconPlay } from 'assets/icons/icon-play.svg';
import { ReactComponent as IconStop } from 'assets/icons/icon-stop.svg';

import styles from './PageRecord.module.css';

interface IPageRecord {
  data?: IPageRecordData,
}

const formatTime = (seconds: number) => {
  const hh = Math.floor(seconds / 3600);
  const mm = Math.floor(seconds / 60) % 60;
  const ss = Math.floor(seconds) % 60;
  return `${(hh ? `${(hh < 10 ? '0' : '') + hh}:` : '') + ((mm < 10) ? '0' : '') + mm}:${(ss < 10) ? '0' : ''}${ss}`;
};

const PageRecord: React.FC<IPageRecord> = memo((props) => {
  const {
    data,
  } = props;

  const { isOpened } = useFlipbookContext();
  const [duration, setDuration] = useState(0);
  const actions = useDispatcher();

  const { containerRef: visualRef } = useMediaResource(data?.items?.visual?.media, useMemo(() => ({
    beforeStart: (target) => {
      target.loop(data?.items?.visual?.loop as boolean);
      if (data?.items?.visual?.loop) {
        target.mute();
      }
    },
  }), [data?.items?.visual?.loop]));

  const {
    mediaRef: recordMediaRef,
    mediaPlayerRef: recordMediaPlayerRef,
    containerRef: recordRef,
  } = useMediaResource(data?.items.voice?.audio, useMemo(() => ({
    beforeStart: (target) => {
      target.loop(false);
    },
    startOnMount: false,
    handleMediaEnd: () => {
      actions.hint.show();
    },
  }), [actions.hint]));

  const handlePlayPause = useCallback(() => {
    if (!recordMediaPlayerRef?.current?.isPlaying()) {
      recordMediaPlayerRef?.current?.play();
    } else {
      recordMediaPlayerRef?.current?.stop();
      setDuration(0);
    }
  }, [recordMediaPlayerRef]);

  const handleAudioProgress = useCallback((event: any) => {
    const { type } = event;
    const { getCurrentTime } = event.target;
    const value = type === WaveSurfer.EVENT.AUDIO_PROCESS ? getCurrentTime() : 0;
    setDuration(value);
  }, []);

  useEffect(() => {
    const mediaPlayer = recordMediaPlayerRef.current;
    if (isOpened) {
      mediaPlayer?.play();
    }
    return () => {
      if (isOpened) {
        mediaPlayer?.stop();
      }
    };
  }, [isOpened, recordMediaPlayerRef]);

  useEffect(() => {
    const media = (recordMediaRef.current as WaveSurfer);
    if (media) {
      media.addEventListener(WaveSurfer.EVENT.AUDIO_PROCESS, handleAudioProgress);
      media.addEventListener(WaveSurfer.EVENT.FINISH, handleAudioProgress);
    }
    return () => {
      if (media) {
        media.removeEventListener(WaveSurfer.EVENT.AUDIO_PROCESS, handleAudioProgress);
        media.removeEventListener(WaveSurfer.EVENT.FINISH, handleAudioProgress);
      }
    };
  }, [recordMediaRef, handleAudioProgress]);

  useEffect(() => {
    actions.hint.hide();
  }, [actions.hint]);

  const layoutWidth = 320;
  const layoutHeight = 480;
  const maskWidth = 320 * 0.95;
  const maskHeight = (maskWidth / 2) * 3;

  const maskStyle = {
    width: `${maskWidth / 10}rem`,
    height: `${maskHeight / 10}rem`,
  };

  const bodyStyle = {
    left: `-${((layoutWidth - maskWidth) / 4) / 10}rem`,
    top: `-${((layoutHeight - maskHeight) / 4) / 10}rem`,
    width: `${(layoutWidth - ((layoutWidth - maskWidth) / 2)) / 10}rem`,
    height: `${(layoutHeight - ((layoutHeight - maskHeight) / 2)) / 10}rem`,
  };

  const visualStyle = {
    filter: data?.items.visual?.filter,
  };

  return (
    <div className={styles.componentPageRecord}>
      <div className={styles.mask} style={maskStyle}>
        <div className={styles.body} style={bodyStyle}>
          {data?.items?.visual && (
            <div className={styles.itemVisual} style={visualStyle} ref={visualRef} />
          )}
          {data?.items?.voice && (
            <div className={styles.itemWaveform}>
              <div className={styles.waveform} ref={recordRef} />
              <div className={styles.duration}>{formatTime(duration)}</div>
              <div className={styles.fill} />
              <button type="button" className={styles.button} onClick={handlePlayPause}>
                {!recordMediaPlayerRef?.current?.isPlaying()
                  ? <div className={styles.iconPlay}><IconPlay /></div>
                  : <div className={styles.iconStop}><IconStop /></div>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default PageRecord;
